/**
 * Client-side file downloads.
 *
 * CSV rather than a real .xlsx: the source app pulled in SheetJS to write
 * workbooks, and a spreadsheet library is a large dependency to carry for
 * "let me open this in Excel". CSV opens in Excel, Numbers and Sheets, and
 * costs nothing. The trade-off is no column widths, no bold header row and no
 * second sheet — if any of those turn out to matter, that is the point at which
 * the dependency earns its place.
 */

/** Escapes one cell. Quotes are doubled; anything containing a comma, a quote
 *  or a newline is quoted. A leading =, +, - or @ is prefixed with a tab so
 *  Excel treats it as text rather than a formula. */
function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (/^[=+\-@]/.test(text)) text = `\t${text}`;
  return /[",\r\n\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(rows: unknown[][]): string {
  /* CRLF and a BOM: Excel on Windows needs both to read UTF-8 accented names
     correctly, and this app has client names with accents in them. */
  return `﻿${rows.map((row) => row.map(cell).join(',')).join('\r\n')}`;
}

export function downloadFile(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  /* Revoked on the next tick, not immediately — Safari cancels the download if
     the object URL disappears before it has read it. */
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const downloadCsv = (filename: string, rows: unknown[][]): void =>
  downloadFile(filename, toCsv(rows));

/** Local date parts, not toISOString(), so an evening export west of Greenwich
 *  is not stamped with tomorrow's date. */
/**
 * Saves bytes the API produced.
 *
 * The sibling of `downloadFile`, which builds its content from a string in the
 * browser. This one takes a Blob because the file was made server-side and
 * fetched with a bearer token — a Word document, in the one case there is so
 * far — so there is nothing to stringify and nothing to add a BOM to.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  /* Revoked on the next tick, not immediately — see downloadFile. */
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function fileStamp(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const slugForFile = (name: string): string =>
  name.trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'export';
